/* ═══════════════════════════════════════════════════════════════════
   Palladium PageBuilder — keyframe timeline runtime (storefront)

   The editor's Timeline (Advanced mode) saves plain CSS: one @keyframes per
   animated property and a `#id[data-pb-tl]{animation:…}` rule per element,
   wrapped in @media (prefers-reduced-motion: no-preference). Most triggers
   need NO script at all:
     load / loop  → the CSS animation simply plays
     hover        → :hover / :has(#zone:hover) rules
     scrub / page → animation-timeline: view() / scroll(root) (Chrome/Edge 115+, Safari 26+)
   This script only adds what CSS cannot do alone:
     scroll       → members wait (paused) until their zone scrolls into view;
                    data-pb-tl-replay="1" replays on every entry
     scrub / page → fallback for browsers without scroll-driven animations:
                    the playhead follows the zone's progress through the viewport
                    (scrub) or the scroll progress of the whole page (page)

   Markup contract (written by timeline-plugin.js):
     data-pb-tl="<sceneId>"  data-pb-tl-on="load|scroll|hover|loop|scrub|page"
     data-pb-tl-anchor="<id>" (trigger zone, when not the element itself)
     data-pb-tl-range="cover|entry|exit|contain" (scrub)  data-pb-tl-replay="1"

   Without JS, a scroll scene plays on load: content can never stay hidden.
   Works on static-SSR pages and under Blazor circuits (MutationObserver).
   ═══════════════════════════════════════════════════════════════════ */
(function () {
    'use strict';

    if (!document.body || !document.documentElement) return;
    try {
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    } catch (e) { /* matchMedia missing → animate */ }

    // Arms the CSS gate: scroll members stay paused until .pb-tl-play.
    document.documentElement.classList.add('pb-tl-js');

    var hasIO = 'IntersectionObserver' in window;
    var nativeScrub = false;
    try { nativeScrub = !!(window.CSS && CSS.supports && CSS.supports('animation-timeline: view()')); } catch (e) { }

    /** The element's timeline animations (CSS animations named pbtl-…). */
    function tlAnims(el) {
        var out = [];
        try {
            var list = el.getAnimations ? el.getAnimations() : [];
            for (var i = 0; i < list.length; i++) {
                var n = list[i].animationName;
                if (n && n.indexOf('pbtl-') === 0) out.push(list[i]);
            }
        } catch (e) { }
        return out;
    }

    function rewind(el) {
        var a = tlAnims(el);
        for (var i = 0; i < a.length; i++) { try { a[i].currentTime = 0; } catch (e) { } }
    }

    function anchorOf(el) {
        var id = el.getAttribute('data-pb-tl-anchor');
        return (id && document.getElementById(id)) || el;
    }

    // ── scroll: play when the zone enters the viewport ──────────────
    var zones = new Map(); // zone element → [members]
    var io = hasIO ? new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
            var members = zones.get(entries[i].target) || [];
            for (var j = 0; j < members.length; j++) {
                var m = members[j];
                if (entries[i].isIntersecting) {
                    m.classList.add('pb-tl-play');
                } else if (m.getAttribute('data-pb-tl-replay') === '1' && m.classList.contains('pb-tl-play')) {
                    // Left the viewport → re-arm for the next entry.
                    m.classList.remove('pb-tl-play');
                    rewind(m);
                }
            }
        }
    }, { threshold: 0.15, rootMargin: '0px 0px -5% 0px' }) : null;

    function registerScroll(el) {
        if (!io) { el.classList.add('pb-tl-play'); return; }
        rewind(el); // it may have run a few ms before this script armed the gate
        var zone = anchorOf(el);
        var list = zones.get(zone);
        if (!list) { list = []; zones.set(zone, list); io.observe(zone); }
        list.push(el);
    }

    // ── scrub fallback: progress of the zone through the viewport ───
    var scrubbed = [];
    var ticking = false;

    /** Layout top/height of an element, ignoring its own transforms (no feedback loop). */
    function layoutBox(el) {
        var top = 0, cur = el;
        while (cur) { top += cur.offsetTop || 0; cur = cur.offsetParent; }
        var sy = window.pageYOffset || document.documentElement.scrollTop || 0;
        return { top: top - sy, h: el.offsetHeight || 0 };
    }

    /** 0…1 progress for a view-timeline range (cover/entry/exit/contain). */
    function progress(zone, range) {
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var b = layoutBox(zone), a = b.top, h = b.h;
        var containStart = vh - Math.min(h, vh);
        var containEnd = Math.min(0, vh - h);
        var start, end;
        switch (range) {
            case 'entry': start = vh; end = containStart; break;
            case 'exit': start = containEnd; end = -h; break;
            case 'contain': start = containStart; end = containEnd; break;
            default: start = vh; end = -h; // cover
        }
        if (start === end) return a <= end ? 1 : 0;
        var p = (start - a) / (start - end);
        return p < 0 ? 0 : (p > 1 ? 1 : p);
    }

    /** 0…1 scroll progress of the page itself (= animation-timeline: scroll(root)). */
    function pageProgress() {
        var se = document.scrollingElement || document.documentElement;
        var max = (se.scrollHeight || 0) - (window.innerHeight || se.clientHeight || 0);
        if (max <= 0) return 0;
        var p = (window.pageYOffset || se.scrollTop || 0) / max;
        return p < 0 ? 0 : (p > 1 ? 1 : p);
    }

    function updateScrub() {
        ticking = false;
        for (var i = 0; i < scrubbed.length; i++) {
            var el = scrubbed[i];
            if (!el.isConnected) { scrubbed.splice(i--, 1); continue; }
            var p = el.getAttribute('data-pb-tl-on') === 'page' ? pageProgress()
                : progress(anchorOf(el), el.getAttribute('data-pb-tl-range') || 'cover');
            var a = tlAnims(el);
            for (var j = 0; j < a.length; j++) {
                try {
                    if (a[j].playState !== 'paused') a[j].pause();
                    var d = a[j].effect.getComputedTiming().activeDuration;
                    if (isFinite(d)) a[j].currentTime = p * d * 0.9999; // stay inside the active phase
                } catch (e) { }
            }
        }
    }

    function requestScrub() {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(updateScrub);
    }

    var scrubWired = false;
    function registerScrub(el) {
        if (nativeScrub) return; // CSS animation-timeline does it all
        scrubbed.push(el);
        if (!scrubWired) {
            scrubWired = true;
            window.addEventListener('scroll', requestScrub, { passive: true });
            window.addEventListener('resize', requestScrub, { passive: true });
        }
        requestScrub();
    }

    // ── registration ─────────────────────────────────────────────────
    var seen = typeof WeakSet === 'function' ? new WeakSet() : null;
    function register(el) {
        if (seen) { if (seen.has(el)) return; seen.add(el); }
        else { if (el.__pbTl) return; el.__pbTl = 1; }
        var on = el.getAttribute('data-pb-tl-on');
        if (on === 'scroll') registerScroll(el);
        else if (on === 'scrub' || on === 'page') registerScrub(el);
    }

    function scan(scope) {
        if (!scope || !scope.querySelectorAll) return;
        if (scope.hasAttribute && scope.hasAttribute('data-pb-tl')) register(scope);
        var list = scope.querySelectorAll('[data-pb-tl]');
        for (var i = 0; i < list.length; i++) register(list[i]);
    }

    scan(document);

    // Blazor (circuit patches, enhanced nav) and widgets add nodes after load.
    var mo = new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
            var added = muts[i].addedNodes;
            for (var j = 0; j < added.length; j++) {
                if (added[j].nodeType === 1) scan(added[j]);
            }
        }
    });
    mo.observe(document.body, { childList: true, subtree: true });
})();
